import { EditPageTextField, EditPageCheckbox, EditPageSelect, EditPageTextArea } from './../../common/EditPage';
import { update, getOne } from './../../common/v4/BackendProvider';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Panel from '../../common/layout/Panel';

const CampaignsEdit = () => {
    const [data, setData] = useState({});
    const [metadata, setMetadata] = useState({});
    const [contactColumns, setContactColumns] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [contactPreview, setContactPreview] = useState({ count: 0, contacts: [] });
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);
    const navigate = useNavigate();
    const { campaign_id } = useParams();

    // Generate time options with 15-minute increments
    const generateTimeOptions = () => {
        const options = {};
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                options[timeString] = timeString;
            }
        }
        return options;
    };

    const weekdays = [
        { key: 'monday', label: 'Mon' },
        { key: 'tuesday', label: 'Tue' },
        { key: 'wednesday', label: 'Wed' },
        { key: 'thursday', label: 'Thu' },
        { key: 'friday', label: 'Fri' },
        { key: 'saturday', label: 'Sat' },
        { key: 'sunday', label: 'Sun' }
    ];

    useEffect(() => {
        // Fetch campaign data and metadata
        getOne('/admin/campaigns/', campaign_id).then(response => {
            setMetadata(response.data.metadata);
            setData(response.data.data);
        });

        // Fetch contact columns from backend
        getOne('/admin/contacts/columns', '').then(response => {
            if (response.data && response.data.columns) {
                setContactColumns(response.data.columns);
            }
        }).catch(error => {
            console.error('Error fetching contact columns:', error);
        });
    }, [campaign_id]);

    // Fetch contacts when filters change
    useEffect(() => {
        if (data.filters && data.filters.length > 0) {
            fetchContactPreview();
        } else {
            setContactPreview({ count: 0, contacts: [] });
        }
    }, [data.filters]);

    const fetchContactPreview = async () => {
        setIsLoadingContacts(true);
        try {
            const response = await getOne('/admin/contacts', `?filters=${JSON.stringify(data.filters)}&limit=20`);
            if (response.data && response.data.result === 'OK') {
                setContactPreview({
                    count: response.data.count,
                    contacts: response.data.data
                });
            }
        } catch (error) {
            console.error('Error fetching contact preview:', error);
            setContactPreview({ count: 0, contacts: [] });
        } finally {
            setIsLoadingContacts(false);
        }
    };

    const handleSave = () => {
        // Transform data for backend
        const saveData = {
            ...data,
            name: data.name || data.ATRNAME,
            users: data.users || data.users_clean?.map(u => u.id) || []
        };
        
        update('/admin/campaigns/', campaign_id, {data: saveData}).then(response => {
            toast.success("Campaign updated successfully", {});
            navigate('/campaigns/campaigns');
        }).catch(error => {
            toast.error("Error updating Campaign: " + error.message, {});
        });
    }

    const handleCancel = () => {
        navigate('/campaigns/campaigns');
    }

    const onChange = (id, value) => {
        setData({ ...data, [id]: value });
    }

    const criteriaOptions = {
        'equals': 'Equals',
        'contains': 'Contains', 
        'greater': 'Greater Than',
        'lesser': 'Less Than',
        'does_not_contain': 'Does Not Contain'
    };

    // Filter management functions
    const addFilter = () => {
        const newFilter = {
            id: Date.now(),
            column: '',
            criteria: 'equals',
            value: ''
        };
        setData({
            ...data,
            filters: [...(data.filters || []), newFilter]
        });
    };

    const removeFilter = (filterId) => {
        setData({
            ...data,
            filters: (data.filters || []).filter(filter => filter.id !== filterId)
        });
    };

    const updateFilter = (filterId, field, value) => {
        setData({
            ...data,
            filters: (data.filters || []).map(filter => 
                filter.id === filterId ? { ...filter, [field]: value } : filter
            )
        });
    };

    // Schedule management functions
    const addSchedule = () => {
        const newSchedule = {
            id: Date.now(),
            weekdays: {
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false,
                saturday: false,
                sunday: false
            },
            startTime: '09:00',
            endTime: '17:00',
            timezone: metadata?.timezones ? Object.keys(metadata.timezones)[0] : 'UTC'
        };
        setData({
            ...data,
            schedules: [...(data.schedules || []), newSchedule]
        });
    };

    const removeSchedule = (scheduleId) => {
        setData({
            ...data,
            schedules: (data.schedules || []).filter(schedule => schedule.id !== scheduleId)
        });
    };

    const updateSchedule = (scheduleId, field, value) => {
        setData({
            ...data,
            schedules: (data.schedules || []).map(schedule => 
                schedule.id === scheduleId ? { ...schedule, [field]: value } : schedule
            )
        });
    };

    const updateScheduleWeekday = (scheduleId, weekday, checked) => {
        setData({
            ...data,
            schedules: (data.schedules || []).map(schedule => 
                schedule.id === scheduleId 
                    ? { 
                        ...schedule, 
                        weekdays: { 
                            ...schedule.weekdays, 
                            [weekday]: checked 
                        } 
                    } 
                    : schedule
            )
        });
    };

    const renderOverviewTab = () => (
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-body">
                        <h5>Campaign Overview</h5>
                        <p>Campaign overview settings will be configured here.</p>
                        <EditPageTextField 
                            label="Campaign Name" 
                            id="name" 
                            onChange={onChange} 
                            value={data.name || data.ATRNAME || ''} 
                        />
                        <EditPageTextArea 
                            label="Description" 
                            id="description" 
                            onChange={onChange} 
                            value={data.description || ''} 
                        />
                        <EditPageCheckbox 
                            label="Active" 
                            id="status" 
                            onChange={onChange} 
                            value={data.status !== undefined ? data.status : true} 
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderContactsTab = () => (
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-header">
                        <h5>Contact Filters</h5>
                        <p className="text-muted mb-0">Add filters to select contacts for this campaign</p>
                    </div>
                    <div className="card-body">
                        {(!data.filters || data.filters.length === 0) ? (
                            <div className="text-center py-4">
                                <p className="text-muted">No filters added yet. Click "Add Filter" to get started.</p>
                            </div>
                        ) : (
                            <div className="filters-container">
                                {data.filters.map((filter, index) => (
                                    <div key={filter.id} className="filter-row border rounded p-3 mb-3">
                                        <div className="row align-items-end">
                                            <div className="col-md-3">
                                                <div className="form-group">
                                                    <label className="control-label">Column</label>
                                                    <div style={{padding: '0px'}}>
                                                        <EditPageSelect
                                                            id={`filter-${filter.id}-column`}
                                                            value={filter.column}
                                                            onChange={(id, value) => updateFilter(filter.id, 'column', value)}
                                                            options={contactColumns.reduce((acc, col) => {
                                                                acc[col.name] = col.displayName;
                                                                return acc;
                                                            }, {})}
                                                            placeholder="Select column"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group">
                                                    <label className="control-label">Criteria</label>
                                                    <div style={{padding: '0px'}}>
                                                        <EditPageSelect
                                                            id={`filter-${filter.id}-criteria`}
                                                            value={filter.criteria}
                                                            onChange={(id, value) => updateFilter(filter.id, 'criteria', value)}
                                                            options={criteriaOptions}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-group">
                                                    <label className="control-label">Value</label>
                                                    <div style={{padding: '0px'}}>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={filter.value}
                                                            onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                                                            placeholder="Enter filter value"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => removeFilter(filter.id)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="mt-3">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={addFilter}
                            >
                                Add Filter
                            </button>
                        </div>

                        {/* Contact Preview Section */}
                        {data.filters && data.filters.length > 0 && (
                            <div className="mt-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mb-0">Contact Preview</h6>
                                    </div>
                                    <div className="card-body">
                                        {isLoadingContacts ? (
                                            <div className="text-center py-3">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="mt-2 text-muted">Loading contacts...</p>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="mb-3">
                                                    <strong>Total contacts found: {contactPreview.count}</strong>
                                                    {contactPreview.count > 20 && (
                                                        <span className="text-muted ms-2">(showing first 20)</span>
                                                    )}
                                                </div>
                                                
                                                {contactPreview.contacts.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-striped">
                                                            <thead>
                                                                <tr>
                                                                    {contactColumns.slice(0, 5).map((col, index) => (
                                                                        <th key={index}>{col.displayName}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {contactPreview.contacts.map((contact, index) => (
                                                                    <tr key={index}>
                                                                        {contactColumns.slice(0, 5).map((col, colIndex) => (
                                                                            <td key={colIndex}>
                                                                                {contact[col.name] || ''}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-3">
                                                        <p className="text-muted">No contacts found matching the current filters.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderScheduleTab = () => (
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-header">
                        <h5>Campaign Schedule</h5>
                        <p className="text-muted mb-0">Configure when this campaign should be active</p>
                    </div>
                    <div className="card-body">
                        {/* Max Days to Run Setting */}
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="control-label">Max Number of Days to Run</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={data.max_days_to_run || 30}
                                        onChange={(e) => onChange('max_days_to_run', parseInt(e.target.value) || 30)}
                                        min="1"
                                        max="365"
                                        placeholder="30"
                                    />
                                    <small className="form-text text-muted">
                                        Maximum number of days this campaign will run (1-365 days)
                                    </small>
                                </div>
                            </div>
                        </div>

                        {(!data.schedules || data.schedules.length === 0) ? (
                            <div className="text-center py-4">
                                <p className="text-muted">No schedules added yet. Click "Add Schedule" to get started.</p>
                            </div>
                        ) : (
                            <div className="schedules-container">
                                {data.schedules.map((schedule, index) => (
                                    <div key={schedule.id} className="schedule-row border rounded p-3 mb-3">
                                        <div className="row align-items-end">
                                            {/* Weekdays */}
                                            <div className="col-md-4">
                                                <div className="form-group">
                                                    <label className="control-label">Days of Week</label>
                                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                                        {weekdays.map((day) => (
                                                            <div key={day.key} className="form-check form-check-inline">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id={`${schedule.id}-${day.key}`}
                                                                    checked={schedule.weekdays[day.key]}
                                                                    onChange={(e) => updateScheduleWeekday(schedule.id, day.key, e.target.checked)}
                                                                />
                                                                <label 
                                                                    className="form-check-label" 
                                                                    htmlFor={`${schedule.id}-${day.key}`}
                                                                >
                                                                    {day.label}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Start Time */}
                                            <div className="col-md-2">
                                                <div className="form-group">
                                                    <label className="control-label">Start Time</label>
                                                    <div style={{padding: '0px'}}>
                                                        <EditPageSelect
                                                            id={`schedule-${schedule.id}-startTime`}
                                                            value={schedule.startTime}
                                                            onChange={(id, value) => updateSchedule(schedule.id, 'startTime', value)}
                                                            options={generateTimeOptions()}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* End Time */}
                                            <div className="col-md-2">
                                                <div className="form-group">
                                                    <label className="control-label">End Time</label>
                                                    <div style={{padding: '0px'}}>
                                                        <EditPageSelect
                                                            id={`schedule-${schedule.id}-endTime`}
                                                            value={schedule.endTime}
                                                            onChange={(id, value) => updateSchedule(schedule.id, 'endTime', value)}
                                                            options={generateTimeOptions()}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Timezone */}
                                            <div className="col-md-3">
                                                <div className="form-group">
                                                    <label className="control-label">Timezone</label>
                                                    <div style={{padding: '0px'}}>
                                                        <EditPageSelect
                                                            id={`schedule-${schedule.id}-timezone`}
                                                            value={schedule.timezone}
                                                            onChange={(id, value) => updateSchedule(schedule.id, 'timezone', value)}
                                                            options={metadata?.timezones || {}}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Remove Button */}
                                            <div className="col-md-1">
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => removeSchedule(schedule.id)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Schedule Summary */}
                                        <div className="mt-2">
                                            <small className="text-muted">
                                                Active: {Object.entries(schedule.weekdays)
                                                    .filter(([_, checked]) => checked)
                                                    .map(([day, _]) => weekdays.find(w => w.key === day)?.label)
                                                    .join(', ') || 'No days selected'} | 
                                                {schedule.startTime} - {schedule.endTime} ({schedule.timezone})
                                            </small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        <div className="mt-3">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={addSchedule}
                            >
                                Add Schedule
                            </button>
                        </div>
                        
                        {/* Schedule Summary */}
                        {data.schedules && data.schedules.length > 0 && (
                            <div className="mt-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mb-0">Schedule Summary</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-4">
                                                <strong>Total Schedules:</strong> {data.schedules.length}
                                            </div>
                                            <div className="col-md-4">
                                                <strong>Active Days:</strong> {
                                                    [...new Set(data.schedules.flatMap(schedule => 
                                                        Object.entries(schedule.weekdays)
                                                            .filter(([_, checked]) => checked)
                                                            .map(([day, _]) => day)
                                                    ))].length
                                                } unique days
                                            </div>
                                            <div className="col-md-4">
                                                <strong>Max Days to Run:</strong> {data.max_days_to_run || 30} days
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderUsersTab = () => (
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-header">
                        <h5>Campaign Users</h5>
                        <p className="text-muted mb-0">Select users who will be assigned to this campaign</p>
                    </div>
                    <div className="card-body">
                        <div className="form-group">
                            <label className="control-label">Assigned Users</label>
                            <div style={{padding: '0px'}}>
                                <EditPageSelect
                                    id="users"
                                    value={data.users || data.users_clean?.map(u => u.id) || []}
                                    onChange={(id, value) => onChange(id, value)}
                                    options={metadata?.users || {}}
                                    isMulti={true}
                                    placeholder="Select users to assign to this campaign"
                                />
                            </div>
                            <small className="form-text text-muted">
                                Users selected here will be responsible for managing and executing this campaign.
                            </small>
                        </div>
                        
                        {/* Selected Users Summary */}
                        {(data.users || data.users_clean) && (data.users?.length > 0 || data.users_clean?.length > 0) && (
                            <div className="mt-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mb-0">Selected Users ({(data.users || data.users_clean || []).length})</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            {(data.users || data.users_clean || []).map((user, index) => {
                                                const userId = typeof user === 'object' ? user.id : user;
                                                const userName = typeof user === 'object' ? user.name : metadata?.users?.[userId];
                                                return (
                                                    <div key={index} className="col-md-6 col-lg-4 mb-2">
                                                        <div className="d-flex align-items-center p-2 border rounded">
                                                            <div className="flex-shrink-0 me-2">
                                                                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                                     style={{width: '32px', height: '32px'}}>
                                                                    {userName ? userName.charAt(0).toUpperCase() : 'U'}
                                                                </div>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="fw-medium">{userName || `User ${userId}`}</div>
                                                                <small className="text-muted">User ID: {userId}</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* No Users Selected */}
                        {(!data.users || data.users.length === 0) && (!data.users_clean || data.users_clean.length === 0) && (
                            <div className="text-center py-4">
                                <div className="text-muted">
                                    <i className="fas fa-users fa-3x mb-3"></i>
                                    <p>No users selected yet.</p>
                                    <p className="small">Use the dropdown above to select users for this campaign.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderFlowsTab = () => (
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-header">
                        <h5>Campaign Flow</h5>
                        <p className="text-muted mb-0">Select the flow that will be executed for this campaign</p>
                    </div>
                    <div className="card-body">
                        <div className="form-group">
                            <label className="control-label">Flow</label>
                            <div style={{padding: '0px'}}>
                                <EditPageSelect
                                    id="flow_id"
                                    value={data.flow_id}
                                    onChange={(id, value) => onChange(id, value)}
                                    options={metadata?.flows || {}}
                                    placeholder="Select a flow for this campaign"
                                    required={true}
                                />
                            </div>
                            <small className="form-text text-muted">
                                The selected flow will define the sequence of actions and interactions for this campaign.
                            </small>
                        </div>
                        
                        {/* Selected Flow Summary */}
                        {data.flow_id && metadata?.flows?.[data.flow_id] && (
                            <div className="mt-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mb-0">Selected Flow</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center p-3 border rounded">
                                            <div className="flex-shrink-0 me-3">
                                                <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                     style={{width: '48px', height: '48px'}}>
                                                    <i className="fas fa-project-diagram"></i>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-medium fs-5">{metadata.flows[data.flow_id]}</div>
                                                <div className="text-muted">Flow ID: {data.flow_id}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* No Flow Selected */}
                        {!data.flow_id && (
                            <div className="text-center py-4">
                                <div className="text-muted">
                                    <i className="fas fa-project-diagram fa-3x mb-3"></i>
                                    <p>No flow selected yet.</p>
                                    <p className="small">Use the dropdown above to select a flow for this campaign.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderOptionsTab = () => (
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-header">
                        <h5>Campaign Options</h5>
                        <p className="text-muted mb-0">Configure advanced campaign settings and options</p>
                    </div>
                    <div className="card-body">
                        <div className="form-group">
                            <label className="control-label">Number Pool</label>
                            <div style={{padding: '0px'}}>
                                <EditPageSelect
                                    id="number_pool_id"
                                    value={data.number_pool_id}
                                    onChange={(id, value) => onChange(id, value)}
                                    options={metadata?.number_pools || {}}
                                    placeholder="Select a number pool for this campaign"
                                />
                            </div>
                            <small className="form-text text-muted">
                                The selected number pool will provide phone numbers for outbound calls and SMS messages in this campaign.
                            </small>
                        </div>
                        
                        {/* Selected Number Pool Summary */}
                        {data.number_pool_id && metadata?.number_pools?.[data.number_pool_id] && (
                            <div className="mt-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mb-0">Selected Number Pool</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center p-3 border rounded">
                                            <div className="flex-shrink-0 me-3">
                                                <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                     style={{width: '48px', height: '48px'}}>
                                                    <i className="fas fa-phone"></i>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-medium fs-5">{metadata.number_pools[data.number_pool_id]}</div>
                                                <div className="text-muted">Pool ID: {data.number_pool_id}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* No Number Pool Selected */}
                        {!data.number_pool_id && (
                            <div className="text-center py-4">
                                <div className="text-muted">
                                    <i className="fas fa-phone fa-3x mb-3"></i>
                                    <p>No number pool selected yet.</p>
                                    <p className="small">Use the dropdown above to select a number pool for this campaign.</p>
                                </div>
                            </div>
                        )}
                        
                        {/* Additional Options Section */}
                        <div className="mt-4">
                            <h6>Additional Options</h6>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <EditPageCheckbox 
                                            label="Enable Retry Logic" 
                                            id="enable_retry" 
                                            onChange={onChange} 
                                            value={data.enable_retry || false} 
                                        />
                                        <small className="form-text text-muted">
                                            Automatically retry failed calls or messages
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'contacts', label: 'Contacts' },
        { id: 'schedule', label: 'Schedule' },
        { id: 'users', label: 'Users' },
        { id: 'flows', label: 'Flows' },
        { id: 'options', label: 'Options' }
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return renderOverviewTab();
            case 'contacts':
                return renderContactsTab();
            case 'schedule':
                return renderScheduleTab();
            case 'flows':
                return renderFlowsTab();
            case 'options':
                return renderOptionsTab();
            case 'users':
                return renderUsersTab();
            default:
                return renderOverviewTab();
        }
    };
    
    return (
        <Panel title="Edit Campaign">
            <div>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
            
            {/* Tab Navigation */}
            <div className="row mb-4">
                <div className="col-12">
                    <ul className="nav nav-tabs">
                        {tabs.map((tab) => (
                            <li key={tab.id} className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                    type="button"
                                >
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Tab Content */}
            <div className="row">
                <div className="col-12">
                    {renderTabContent()}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                        >
                            Update Campaign
                        </button>
                    </div>
                </div>
            </div>
            </div>
        </Panel>
    );
};

export default CampaignsEdit;
